import React, { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ActionIcon, Button, rem, Stack, Group, Text } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { IconArrowLeft } from '@tabler/icons-react';
import { useGetTaskSummaries } from '../../entities/Tasks/queries/useGetTaskSummaries.tsx';
import { useIdFromRoute } from '../../hooks/useIdFromRoute.tsx';
import { TaskType } from '../../types';
import { GuidelinesHelpIcon } from '../GuidelinesHelpIcon/GuidelinesHelpIcon.tsx';

type TitleButtonProps = {
    label?: string;
    subLabel?: string;
    urlFallback?: string;
    taskType?: TaskType;
    children: React.ReactNode;
    showGuidelinesHelpIcon?: boolean;
};

const TitleButton: React.FC<TitleButtonProps> = ({ label, subLabel, urlFallback, taskType, children, showGuidelinesHelpIcon = true }) => {
    const navigate = useNavigate();

    // Add media query hooks to detect small portrait screens
    const isSmallScreen = useMediaQuery('(max-width: 767px)');
    const isPortrait = useMediaQuery('(orientation: portrait)');
    const isTouchDevice = useMediaQuery('(pointer: coarse)');
    const isMobilePortrait = isSmallScreen && isPortrait && isTouchDevice;

    const { data: taskSummaries } = useGetTaskSummaries();
    const id = useIdFromRoute();

    const currentTaskType = useMemo(() => {
        if (taskType === undefined) 
            return;
        
        switch (taskType) {
            case 0:
                return taskSummaries?.find((task) => task.collaboratorID === Number(id))?.type;
            case TaskType.questionnaire:
            case TaskType.tdm:
                return taskSummaries?.find((task) => task.id === Number(id))?.type;
            default:
                return label;
        }
    }, [taskType, taskSummaries, id, label]);

    return (
        <Stack id={id} w={'100%'} align={'flex-start'} pt="xl" gap="lg">
            <Group justify="space-between" w="100%">
                <Stack gap={4} align="flex-start">
                    <Button
                        onClick={() => (urlFallback ? navigate(urlFallback) : null)}
                        variant="subtle"
                        color="black"
                        leftSection={
                            urlFallback ? (
                                <ActionIcon variant="transparent" color="blue">
                                    <IconArrowLeft />
                                </ActionIcon>
                            ) : null
                        }
                        px={0}
                        styles={{
                            root: { pointerEvents: urlFallback ? 'auto' : 'none' },
                            label: {
                                fontSize: rem('24px'),
                                lineHeight: rem('32px'),
                                background: 'none',
                            },
                        }}
                    >
                        {currentTaskType || label}
                    </Button>
                    {subLabel && (
                        <Text size="sm" c="dimmed" pl={0}>
                            {subLabel}
                        </Text>
                    )}
                </Stack>
                {showGuidelinesHelpIcon && !isMobilePortrait && <GuidelinesHelpIcon />}
            </Group>
            {children}
        </Stack>
    );
};

export default TitleButton;
